import { ThrowError } from "@/api/callApi";
import CommonModal from "@/components/common/CommonModal";
import CommonTable, { type TableColumn } from "@/components/common/CommonTable";
import MainHeading from "@/components/common/MainHeading";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import type { campus } from "@/interface/campus.interface";
import { CampusService } from "@/services/Campus/CampusService";
import { GetFilterUrl, showToastNotification } from "@/utility/helper";
import { RoutesLink } from "@/utility/RouteLinks";
import type { IFilter, IPageInfo } from "@/interface/common.interface";
import TablePagination from "@/components/common/TablePagination";
import { Pencil, Trash, TriangleAlert, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CommonFilter from "@/components/common/CommonFilter";
import { SiteService } from "@/services/Site/SiteServive";
import SearchSelect from "@/components/common/SearchSelect";

const CampusConfig = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [campusData, setCampusData] = useState<campus[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string>("");

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const [sites, setSites] = useState<{ value: string; label: string }[]>([]);

  const [filter, setFilter] = useState<IFilter & { siteId?: string }>({
    search: "",
    siteId: "",
  });

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await SiteService.getSite();
        if (response?.status && response?.data?.site) {
          setSites(response.data.site.map((s: any) => ({ value: s.id, label: s.name })));
        }
      } catch (err) {
        console.error("Error fetching sites:", err);
      }
    };
    fetchSites();
  }, []);

  const handleFilterChange = (value: string, name: string) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (value: string) => {
    setFilter((prev) => {
      if (prev.search === value) return prev;
      return { ...prev, search: value };
    });
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleChangePageSize = (value: number) => {
    setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
  };

  const getCampus = async (
    currentPage = paginationData.currentPage,
    currentLimit = paginationData.pageSize,
  ) => {
    setIsLoading(true);
    try {
      const apiFilter = {
        ...filter,
      };
      const paramsURL = GetFilterUrl(apiFilter);
      const PATH = `pageNumber=${currentPage}&pageSize=${currentLimit}${paramsURL ? `&${paramsURL}` : ""}`;
      const response = await CampusService.getCampus(PATH);
      if (response?.status) {
        setCampusData(response?.data?.campus || []);
        setPaginationData((prev) => ({
          ...prev,
          total: response?.data?.pageInfo?.total || 0,
          totalPages: response?.data?.pageInfo?.totalPages || 0,
        }));
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getCampus(paginationData.currentPage, paginationData.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationData.currentPage, paginationData.pageSize, filter]);

  const toggleDeleteDialog = (id: string | undefined) => {
    setDeleteId(id ?? "");
    setDeleteDialogOpen(!deleteDialogOpen);
  };

  const handleDeleteCampus = async () => {
    try {
      const response = await CampusService.deleteCampus(deleteId);
      if (response?.status) {
        showToastNotification("success", response?.message);
        toggleDeleteDialog("");
        getCampus();
      } else {
        showToastNotification("danger", response?.message);
      }
    } catch (error) {
      ThrowError(error);
    }
  };

  const columns: TableColumn<campus>[] = [
    {
      key: "srNo",
      header: "Sr No",
      render: (_item: campus, index: number) => (
        <span className="text-muted-foreground font-medium"># {index + 1}</span>
      ),
    },
    {
      key: "aliasId",
      header: "Alias ID",
      render: (item: campus) => (
        <span className="font-semibold capitalize">{item?.aliasId || "N/A"}</span>
      )
    },
    {
      key: "campusName",
      header: "Campus Name",
      render: (item: campus) => (
        <span className="font-semibold capitalize">{item?.name}</span>
      ),
    },
    {
      key: "siteName",
      header: "Site Name",
      render: (item: campus) => (
        <span className="font-semibold capitalize">{item?.site?.name}</span>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (item: campus) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.status ? "text-green-600 border-green-600 bg-green-50" : "text-destructive border-destructive bg-destructive/10"}`}
        >
          {item?.status ? "True" : "False"}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (item: campus) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0 hover:bg-muted">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4 text-slate-500" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-[170px] rounded-xl shadow-lg border-slate-100"
          >
            <DropdownMenuItem
              onClick={() => navigate(`${RoutesLink.editCampus}/${item?.id}`)}
              className="cursor-pointer "
            >
              <Pencil className="h-4 w-4" />
              Edit Campus
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => toggleDeleteDialog(item?.id)}
              className="cursor-pointer text-red-600 "
            >
              <Trash className="h-4 w-4" />
              Delete Campus
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const siteOptions = [
    { value: "", label: "All Sites" },
    ...sites
  ];

  const additionalFilters = (
    <div className=" text-left">
      <SearchSelect
        placeholder="Select Site"
        options={siteOptions}
        value={filter.siteId}
        onChange={(val) => handleFilterChange(val, "siteId")}
      />
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6">
        <MainHeading
          title="Campus"
          buttonText="Add Campus"
          onClick={() => navigate(RoutesLink.addCampus)}
          isBackButtonVisible={true}
        />

        <CommonFilter
          searchValue={filter.search || ""}
          onSearchChange={handleSearchChange}
          additionalFilters={additionalFilters}
        />
        <CommonTable data={campusData} columns={columns} loading={isLoading} />
        <TablePagination
          paginationData={paginationData}
          setPaginationData={setPaginationData}
          handleChangePageSize={handleChangePageSize}
          isLoading={isLoading}
        />
      </div>
      <CommonModal
        title=""
        show={deleteDialogOpen}
        onClose={() => toggleDeleteDialog("")}
        className="max-w-md rounded-2xl p-0 overflow-hidden"
      >
        <div className="p-6">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <TriangleAlert className="text-red-600 w-8 h-8" />
            </div>

            <h2 className="mt-5 text-2xl font-bold text-gray-900">
              Delete Campus?
            </h2>

            <p className="mt-3 text-sm leading-6 text-gray-500">
              This action cannot be undone. Once deleted, all campus data will
              be permanently removed from our servers.
            </p>
          </div>

          <div className="mt-8 flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => toggleDeleteDialog("")}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={handleDeleteCampus}
            >
              Delete
            </Button>
          </div>
        </div>
      </CommonModal>
    </Layout>
  );
};

export default CampusConfig;
